import { Share, ShareOptions } from "@capacitor/share";
import { Capacitor } from "@capacitor/core";

export type ShareMethod = "whatsapp" | "telegram" | "sms" | "native" | "copy";

export interface ShareContent {
  text?: string;
  title?: string;
  url?: string;
  files?: string[];
}

export interface ShareOptionsConfig {
  method?: ShareMethod;
  phoneNumber?: string;
  dialogTitle?: string;
}

const formatPhoneNumber = (phoneNumber: string): string => {
  const phoneOnlyNumbers = phoneNumber.replace(/\D/g, "");
  return `+${phoneOnlyNumbers}`;
};

const canShare = async (): Promise<boolean> => {
  const shareCheck = await Share.canShare();
  return shareCheck.value;
};

export const shareContent = async (content: ShareContent, options: ShareOptionsConfig = {}): Promise<void> => {
  const { method = "native", phoneNumber, dialogTitle } = options;
  const { text = "", title = "", url = "", files = [] } = content;

  switch (method) {
    case "whatsapp":
      if (!phoneNumber) throw new Error("Phone number is required for WhatsApp sharing");
      const whatsappUrl = `https://wa.me/${formatPhoneNumber(phoneNumber)}?text=${encodeURIComponent(text)}`;
      window.open(whatsappUrl, "_blank");
      break;

    case "telegram":
      if (!phoneNumber) throw new Error("Phone number is required for Telegram sharing");
      const telegramUrl = `https://t.me/${formatPhoneNumber(phoneNumber)}?text=${encodeURIComponent(text)}`;
      window.open(telegramUrl, "_blank");
      break;

    case "sms":
      if (!phoneNumber) throw new Error("Phone number is required for SMS sharing");
      let smsUrl;
      if (Capacitor.getPlatform() === "ios") {
        smsUrl = `sms:${formatPhoneNumber(phoneNumber)}&body=${encodeURIComponent(text)}`;
      } else {
        smsUrl = `sms:${formatPhoneNumber(phoneNumber)}?body=${encodeURIComponent(text)}`;
      }
      window.open(smsUrl, "_blank");
      break;

    case "native":
      if (await canShare()) {
        const shareData: ShareOptions = {
          title,
          text: text + (url ? `\n${url}` : ""),
          dialogTitle,
          files,
        };
        await Share.share(shareData);
      }
      break;

    case "copy":
      if (navigator.clipboard) {
        await navigator.clipboard.writeText(text + (url ? `\n${url}` : ""));
      }
      break;

    default:
      throw new Error(`Unsupported share method: ${method}`);
  }
};

export const getSharingOptions = (content: ShareContent, options: ShareOptionsConfig = {}, onCancel?: () => void) => {
  return {
    header: "Share",
    message: "Choose sharing method",
    cssClass: "sharing-alert",
    buttons: [
      {
        text: "WhatsApp",
        cssClass: "sharing-button whatsapp-button",
        handler: () => shareContent(content, { ...options, method: "whatsapp" }),
      },
      {
        text: "Telegram",
        cssClass: "sharing-button telegram-button",
        handler: () => shareContent(content, { ...options, method: "telegram" }),
      },
      {
        text: "SMS",
        cssClass: "sharing-button sms-button",
        handler: () => shareContent(content, { ...options, method: "sms" }),
      },
      {
        text: "Native Share",
        cssClass: "sharing-button native-button",
        handler: () => shareContent(content, { ...options, method: "native" }),
      },
      {
        text: "Copy",
        cssClass: "sharing-button copy-button",
        handler: () => shareContent(content, { ...options, method: "copy" }),
      },
      {
        text: "Cancel",
        cssClass: "sharing-button cancel-button",
        role: "cancel",
        handler: onCancel,
      },
    ],
  };
};
